import useGetMyAccountPipelines from '@/api/queries/me/useGetMyAccountPipelines';
import {
  getScheduleDefaultPipelineIdLocalStorageKey,
  getScheduleDefaultPipelineStagesLocalStorageKey,
} from '@/helpers/localStorageKeyHelper';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { useAccountUser } from '@/api/queries/me/useAccountUser';
import {
  AvatarSizeVariant,
  Button,
  Card,
  CardButton,
  ConditionalWrapper,
  EmptyState,
  Select,
  SelectSizeVariant,
  UserAvatar,
} from '@payaca/ambrosia';
import ErrorBoundary from '@payaca/components/errorBoundary/ErrorBoundary';

import { getAddressAsString } from '@payaca/helpers/locationHelper';
import { PipelinesPermissions } from '@payaca/permissions/pipelines/pipelines.permissions';
import { requestGetListedDealsPage } from '@payaca/store/deals/dealsActions';
import {
  GetListedDealsRequestData,
  ListedDeal,
  SortBy,
} from '@payaca/types/listedDealTypes';
import { SortDirection } from '@payaca/types/listViewTypes';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

const UnscheduledDeals: FC<{
  onClickDeal?: (deal: ListedDeal) => void;
  externalDealChangeTimestamp?: Date;
}> = ({ onClickDeal, externalDealChangeTimestamp }) => {
  const dispatch = useDispatch();

  const { current: initalPipelineId } = useRef(
    localStorage.getItem(getScheduleDefaultPipelineIdLocalStorageKey())
      ? Number(
          localStorage.getItem(getScheduleDefaultPipelineIdLocalStorageKey())
        )
      : 0
  );

  const { current: initialPipelineStages } = useRef<string[]>(
    localStorage
      .getItem(getScheduleDefaultPipelineStagesLocalStorageKey())
      ?.split(',')
      .filter(Boolean) ?? []
  );

  const [isLoading, setIsLoading] = useState(
    !!initalPipelineId && initialPipelineStages.length > 0
  );

  const [getListedDealsRequestData, setGetListedDealsRequestData] =
    useState<GetListedDealsRequestData>({
      sortBy: SortBy.LAST_UPDATED,
      sortDirection: SortDirection.DESCENDING,
      pageSize: 50,
      pageNumber: 1,
      pipelineId: initalPipelineId,
      pipelineStages: initialPipelineStages,
      archived: false,
      excludeDealsWithUpcomingEvents: true,
    });
  const [canLoadMoreDeals, setCanLoadMoreDeals] = useState(false);

  const [listedDeals, setListedDeals] = useState<ListedDeal[]>([]);

  const { data: pipelines = [] } = useGetMyAccountPipelines();
  const selectedPipeline = useMemo(
    () =>
      pipelines.find(
        (p) => Number(p.id) === getListedDealsRequestData.pipelineId
      ),
    [pipelines, getListedDealsRequestData.pipelineId]
  );

  const pipelineDropdownOptions = useMemo(() => {
    return [
      ...pipelines.map((p) => ({
        label: p.title,
        value: Number(p.id),
      })),
    ];
  }, [pipelines]);

  useEffect(() => {
    setGetListedDealsRequestData((x) => ({
      ...x,
      pageNumber: 1,
    }));
  }, [externalDealChangeTimestamp]);

  const noFiltersSelected =
    !getListedDealsRequestData.pipelineId ||
    !getListedDealsRequestData.pipelineStages?.length;

  useEffect(() => {
    localStorage.setItem(
      getScheduleDefaultPipelineIdLocalStorageKey(),
      getListedDealsRequestData.pipelineId?.toString() || '0'
    );

    localStorage.setItem(
      getScheduleDefaultPipelineStagesLocalStorageKey(),
      getListedDealsRequestData.pipelineStages?.join(',') ?? ''
    );

    if (noFiltersSelected) {
      setListedDeals([]);
      setCanLoadMoreDeals(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    dispatch(
      requestGetListedDealsPage(
        getListedDealsRequestData,
        (listedDealsListViewPage) => {
          if (getListedDealsRequestData.pageNumber === 1) {
            setListedDeals(listedDealsListViewPage.items);
          } else {
            setListedDeals((x) => [...x, ...listedDealsListViewPage.items]);
          }

          setCanLoadMoreDeals(
            listedDealsListViewPage.totalItemCount >
              getListedDealsRequestData.pageNumber *
                getListedDealsRequestData.pageSize
          );
          setIsLoading(false);
        }
      )
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [getListedDealsRequestData]);

  return (
    <div>
      <PermissionGuard
        renderIfHasPermissions={[
          PipelinesPermissions.GET_PIPELINES_FOR_ACCOUNT,
        ]}
      >
        <div className="mb-4 flex gap-4">
          <div className="min-w-0 shrink grow basis-1/2">
            <ErrorBoundary>
              <Select
                sizeVariant={SelectSizeVariant.SM}
                value={getListedDealsRequestData.pipelineId}
                onChange={(value) =>
                  setGetListedDealsRequestData((x) => {
                    const p = pipelines.find((p) => Number(p.id) === value);

                    return {
                      ...x,
                      pipelineId: value as number,
                      pipelineStages: [p?.stages[0].title || ''],
                      pageNumber: 1,
                    };
                  })
                }
                options={pipelineDropdownOptions}
                CustomOption={(option) => {
                  const pipeline = pipelines.find(
                    (p) => Number(p.id) === option.option.value
                  );
                  return pipeline ? <PipelinePill pipeline={pipeline} /> : null;
                }}
                CustomSelected={({ selectedOptions }) => {
                  const option = selectedOptions?.length
                    ? selectedOptions[0]
                    : null;
                  if (!option) return null;
                  const pipeline = pipelines.find(
                    (p) => Number(p.id) === option.value
                  );
                  return pipeline ? <PipelinePill pipeline={pipeline} /> : null;
                }}
                placeholder={'Pipeline'}
              />
            </ErrorBoundary>
          </div>
          <div className="min-w-0 shrink grow basis-1/2">
            <ErrorBoundary>
              <Select
                sizeVariant={SelectSizeVariant.SM}
                multiple={true}
                value={getListedDealsRequestData.pipelineStages}
                onChange={(value) =>
                  setGetListedDealsRequestData((x) => {
                    return {
                      ...x,
                      pipelineStages: value,
                      pageNumber: 1,
                    };
                  })
                }
                options={
                  selectedPipeline?.stages.map((stage) => ({
                    label: stage.title,
                    value: stage.title,
                  })) || []
                }
                placeholder={'Pipeline stage'}
                CustomSelected={({ selectedOptions }) => {
                  return <>{selectedOptions.map((o) => o.label).join(', ')}</>;
                }}
              />
            </ErrorBoundary>
          </div>
        </div>
      </PermissionGuard>
      {!isLoading && !listedDeals.length && (
        <EmptyState
          text={
            noFiltersSelected
              ? 'Select a pipeline and stage to view unscheduled projects'
              : "You don't have any unscheduled projects matching the filters"
          }
        />
      )}
      <ul className="m-0 mb-8 list-none p-0">
        {listedDeals.map((listedDeal) => (
          <li key={listedDeal.dealId} className="mb-2 last:mb-0">
            <ListedDealCard
              listedDeal={listedDeal}
              onClick={() => onClickDeal?.(listedDeal)}
            />
          </li>
        ))}
      </ul>
      {canLoadMoreDeals && (
        <div className="flex items-center justify-center">
          <Button
            onClick={() => {
              if (isLoading) return;
              setGetListedDealsRequestData((x) => ({
                ...x,
                pageNumber: x.pageNumber + 1,
              }));
            }}
          >
            Load more
          </Button>
        </div>
      )}
    </div>
  );
};

const PipelinePill: FC<{
  pipeline: { title: string; colour: string };
}> = ({ pipeline }) => (
  <div className="flex items-center gap-2 text-left">
    <div
      className="size-4 shrink-0 rounded-full"
      style={{ backgroundColor: pipeline.colour }}
    />
    <span>{pipeline.title}</span>
  </div>
);

const ListedDealCard: FC<{
  listedDeal: ListedDeal;
  onClick?: () => void;
}> = ({ listedDeal, onClick }) => {
  const history = useHistory();
  const user = useAccountUser(listedDeal.assignedToUserId);

  const addressAsString = getAddressAsString(listedDeal.siteAddresses?.[0]);

  const cardProps = {
    onClick,
  };

  return (
    <ConditionalWrapper
      condition={!!onClick}
      Wrapper={CardButton}
      wrapperProps={cardProps}
      FallbackWrapper={Card}
      fallbackWrapperProps={cardProps}
    >
      <Card.Body>
        <span className="flex flex-wrap-reverse items-baseline gap-x-4 gap-y-1">
          <h3 className="mb-0 text-base">{listedDeal.customerName}</h3>

          <span className="ml-auto">
            <Link to={`/projects/${listedDeal.dealId}`}>
              {/* TODO: should use project publicId */}
              <Button size={'xs'} variant="secondary">
                #{listedDeal.reference}
              </Button>
            </Link>
          </span>
        </span>
        {(addressAsString?.length || !!user) && (
          <div className="mt-2 flex gap-4">
            <p className="mb-0">{addressAsString}</p>

            {user && (
              <div className="ml-auto">
                <UserAvatar
                  sizeVariant={AvatarSizeVariant.XS}
                  user={{
                    firstName: user.firstname,
                    lastName: user.lastname,
                    emailAddress: user.email,
                    userColour: user.userColour,
                    imgSrc: user.avatarUrl,
                  }}
                />
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </ConditionalWrapper>
  );
};

export default UnscheduledDeals;
